<script setup lang="ts">
  import { computed } from 'vue';
  import ZoomInIcon from '@/shared/assets/icons/ZoomIn.svg';
  import ZoomOutIcon from '@/shared/assets/icons/ZoomOut.svg';
  import MapIcon from '@/shared/assets/icons/MapIcon.svg';
  import ChartIcon from '@/shared/assets/ChartIcon.svg';
  import RedoIcon from '@/shared/assets/icons/RedoIcon.svg';
  import UndoIcon from '@/shared/assets/icons/UndoIcon.svg';
  import { IconImage } from '@/shared/ui';
  import CanvasOrientation from '../elements/elements/CanvasOrientation.vue';
  import FormBuilderFieldCount from './FormBuilderFieldCount.vue';

  interface Props {
    zoom: number;
    orientation: 'portrait' | 'landscape';
    pageCount: number;
    currentPage?: number;
    mode?: 'builder' | 'rules' | 'view' | 'permissions';
    totalFields?: number;
    totalFieldsScope?: 'matrix' | 'sheet' | 'permissions';
    canUndo?: boolean;
    canRedo?: boolean;
    showAnchorHighlights?: boolean;
    showReportHighlights?: boolean;
    /**
     * Releases the anchor fields button outside the rules stage. Off by default, so the screens
     * that mounted this footer before keep the footer they have today.
     */
    enableAnchorTool?: boolean;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    'update:zoom': [value: number];
    'update:orientation': [value: 'portrait' | 'landscape'];
    undo: [];
    redo: [];
    'toggle-anchor-visibility': [];
    'toggle-report-visibility': [];
  }>();

  const PRESETS = [50, 75, 100, 125, 150, 175, 200];
  const MIN_ZOOM_PERCENT = 50;
  const MID_ZOOM_PERCENT = 100;
  const MAX_ZOOM_PERCENT = 200;
  const STEP = 10;
  const SLIDER_MIN_POSITION = 0;
  const SLIDER_MID_POSITION = 50;
  const SLIDER_MAX_POSITION = 100;

  function percentToSliderPosition(percent: number) {
    if (percent <= MID_ZOOM_PERCENT) {
      return (
        ((percent - MIN_ZOOM_PERCENT) / (MID_ZOOM_PERCENT - MIN_ZOOM_PERCENT)) * SLIDER_MID_POSITION
      );
    }
    return (
      SLIDER_MID_POSITION +
      ((percent - MID_ZOOM_PERCENT) / (MAX_ZOOM_PERCENT - MID_ZOOM_PERCENT)) *
        (SLIDER_MAX_POSITION - SLIDER_MID_POSITION)
    );
  }

  function sliderPositionToPercent(position: number) {
    if (position <= SLIDER_MID_POSITION) {
      return (
        MIN_ZOOM_PERCENT +
        (position / SLIDER_MID_POSITION) * (MID_ZOOM_PERCENT - MIN_ZOOM_PERCENT)
      );
    }
    return (
      MID_ZOOM_PERCENT +
      ((position - SLIDER_MID_POSITION) / (SLIDER_MAX_POSITION - SLIDER_MID_POSITION)) *
        (MAX_ZOOM_PERCENT - MID_ZOOM_PERCENT)
    );
  }

  const zoomPercent = computed(() => Math.round(props.zoom * 100));
  const sliderPosition = computed(() => percentToSliderPosition(zoomPercent.value));
  const sliderTrackStyle = computed(() => ({
    background: `linear-gradient(to right, var(--theme-secondary) 0%, var(--theme-secondary) ${sliderPosition.value}%, var(--color-gray-02) ${sliderPosition.value}%, var(--color-gray-02) 100%)`,
  }));
  const showOrientation = computed(
    () => props.mode !== 'rules' && props.mode !== 'view' && props.mode !== 'permissions'
  );
  const showHistory = computed(() => props.mode !== 'view');
  const showFieldCount = computed(
    () => props.mode === 'rules' || props.mode === 'view' || props.mode === 'permissions'
  );
  const showRulesTools = computed(() => props.mode === 'rules');
  const showAnchorTool = computed(() => showRulesTools.value || props.enableAnchorTool === true);
  const anchorTooltip = 'Campos Âncora';
  const reportTooltip = 'Campo para Relatório';
  const anchorTooltipOptions = computed(() => ({
    content: anchorTooltip,
    placement: 'top' as const,
    theme: 'custom',
    trigger: 'mouseenter',
    hideOnClick: true,
    offset: [0, 8] as [number, number],
  }));
  const reportTooltipOptions = computed(() => ({
    content: reportTooltip,
    placement: 'top' as const,
    theme: 'custom',
    trigger: 'mouseenter',
    hideOnClick: true,
    offset: [0, 8] as [number, number],
  }));

  const selectValue = computed(() =>
    PRESETS.includes(zoomPercent.value) ? String(zoomPercent.value) : 'custom'
  );

  function emitZoomPercent(percent: number) {
    const clamped = Math.min(MAX_ZOOM_PERCENT, Math.max(MIN_ZOOM_PERCENT, percent));
    emit('update:zoom', clamped / 100);
  }

  function handleSlider(event: Event) {
    const target = event.target as HTMLInputElement;
    emitZoomPercent(Math.round(sliderPositionToPercent(Number(target.value))));
  }

  function handleSelect(event: Event) {
    const target = event.target as HTMLSelectElement;
    if (target.value !== 'custom') {
      emitZoomPercent(Number(target.value));
    }
  }

  function zoomOut() {
    emitZoomPercent(zoomPercent.value - STEP);
  }

  function zoomIn() {
    emitZoomPercent(zoomPercent.value + STEP);
  }

  function handleOrientationUpdate(value: 'portrait' | 'landscape') {
    emit('update:orientation', value);
  }
</script>

<template>
  <footer class="flex h-8 shrink-0 items-center gap-4 border-t border-[var(--color-gray-03)] bg-[var(--color-gray-09)] px-6">
    <div v-if="showOrientation" class="flex items-center gap-2">
      <CanvasOrientation
        :orientation="props.orientation"
        @update:orientation="handleOrientationUpdate"
      />
    </div>

    <div
      v-if="showOrientation"
      class="h-5 w-px shrink-0 bg-[var(--color-gray-02)]"
    />

    <div class="flex items-center gap-1.5">
      <button
        class="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[var(--color-gray-07)] transition-colors duration-150 hover:bg-[var(--color-gray-08)] disabled:cursor-not-allowed disabled:opacity-40"
        :disabled="zoomPercent <= MIN_ZOOM_PERCENT"
        data-testid="zoom-out-btn"
        @click="zoomOut"
      >
        <IconImage :src="ZoomOutIcon" alt="Reduzir zoom" />
      </button>

      <input
        type="range"
        class="h-1 w-[208px] cursor-pointer appearance-none rounded bg-[var(--color-gray-02)] accent-[var(--theme-primary)] outline-none [&::-webkit-slider-thumb]:h-[14px] [&::-webkit-slider-thumb]:w-[14px] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-[1.5px] [&::-webkit-slider-thumb]:border-[var(--color-gray-02)] [&::-webkit-slider-thumb]:bg-[var(--color-white-01)] [&::-webkit-slider-thumb]:shadow-[0_1px_3px_var(--color-black-20)] [&::-moz-range-thumb]:h-[14px] [&::-moz-range-thumb]:w-[14px] [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-[1.5px] [&::-moz-range-thumb]:border-[var(--color-gray-02)] [&::-moz-range-thumb]:bg-[var(--color-white-01)] [&::-moz-range-thumb]:shadow-[0_1px_3px_var(--color-black-20)] dark:[&::-webkit-slider-thumb]:bg-[var(--color-gray-07)] dark:[&::-moz-range-thumb]:bg-[var(--color-gray-07)]"
        :style="sliderTrackStyle"
        :min="SLIDER_MIN_POSITION"
        :max="SLIDER_MAX_POSITION"
        step="1"
        :value="sliderPosition"
        data-testid="zoom-slider"
        @input="handleSlider"
      />

      <button
        type="button"
        class="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[var(--color-gray-07)] transition-colors duration-150 hover:bg-[var(--color-gray-08)] disabled:cursor-not-allowed disabled:opacity-40"
        :disabled="zoomPercent >= MAX_ZOOM_PERCENT"
        data-testid="zoom-in-btn"
        @click="zoomIn"
      >
        <IconImage :src="ZoomInIcon" alt="Aumentar zoom" />
      </button>

      <select
        class="h-6 cursor-pointer rounded-md border border-[var(--color-gray-02)] bg-[var(--color-white-01)] px-1.5 font-[var(--font-base)] text-xs text-[var(--color-gray-07)] outline-none"
        :value="selectValue"
        data-testid="zoom-select"
        @change="handleSelect"
      >
        <option v-if="selectValue === 'custom'" value="custom">{{ zoomPercent }}%</option>
        <option v-for="preset in PRESETS" :key="preset" :value="String(preset)">{{ preset }}%</option>
      </select>
    </div>

    <div v-if="showHistory" class="h-5 w-px shrink-0 bg-[var(--color-gray-02)]" />

    <div v-if="showHistory" class="flex items-center gap-1.5" data-testid="footer-history-actions">
      <button
        v-tippy="{
          content: 'Desfazer',
          placement: 'top',
          theme: 'custom',
          trigger: 'mouseenter focus',
          offset: [0, 8],
        }"
        type="button"
        class="flex size-5.5 shrink-0 items-center justify-center rounded-lg border border-[var(--color-gray-02)] bg-[var(--color-white-01)] text-[var(--color-gray-03)] shadow-[0_1px_2px_rgba(16,24,40,0.05)] transition-colors duration-150 hover:bg-[var(--color-gray-09)] focus:outline-none focus:ring-1 focus:ring-[var(--theme-secondary-ink)] focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-[var(--color-white-01)] [&_svg]:shrink-0 [&_svg]:overflow-visible"
        :disabled="!props.canUndo"
        aria-label="Desfazer"
        data-testid="undo-btn"
        @click="emit('undo')"
      >
        <IconImage :src="UndoIcon" alt="Desfazer" class="size-4.5" />
      </button>

      <button
        v-tippy="{
          content: 'Refazer',
          placement: 'top',
          theme: 'custom',
          trigger: 'mouseenter focus',
          offset: [0, 8],
        }"
        type="button"
        class="flex size-5.5 shrink-0 items-center justify-center rounded-lg border border-[var(--color-gray-02)] bg-[var(--color-white-01)] text-[var(--color-gray-03)] shadow-[0_1px_2px_rgba(16,24,40,0.05)] transition-colors duration-150 hover:bg-[var(--color-gray-09)] focus:outline-none focus:ring-1 focus:ring-[var(--theme-secondary-ink)] focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-[var(--color-white-01)] [&_svg]:shrink-0 [&_svg]:overflow-visible"
        :disabled="!props.canRedo"
        aria-label="Refazer"
        data-testid="redo-btn"
        @click="emit('redo')"
      >
        <IconImage :src="RedoIcon" alt="Refazer" class="size-4.5" />
      </button>
    </div>

    <div class="h-5 w-px shrink-0 bg-[var(--color-gray-02)]" />

    <div class="flex items-center">
      <span class="min-w-10 text-center font-[var(--font-base)] text-xs text-[var(--color-gray-07)]" data-testid="page-count">{{ props.currentPage ?? 1 }}/{{ props.pageCount }}</span>
    </div>

    <template v-if="showAnchorTool">
      <div class="h-5 w-px shrink-0 bg-[var(--color-gray-02)]" />

      <button
        v-tippy="anchorTooltipOptions"
        type="button"
        :class="[
          'flex size-6 shrink-0 items-center justify-center rounded-[6px] border border-[var(--color-gray-02)] shadow-[0_1px_2px_rgba(16,24,40,0.05)] transition-colors duration-150',
          props.showAnchorHighlights
            ? 'bg-[var(--color-gray-07)] text-[var(--color-white-01)]'
            : 'bg-[var(--color-white-01)] text-[var(--color-gray-07)] hover:bg-[var(--color-gray-09)]',
        ]"
        aria-label="Campos Âncora"
        data-testid="anchor-btn"
        @click="emit('toggle-anchor-visibility')"
      >
        <IconImage
          :src="MapIcon"
          alt="Campos Âncora"
          class="size-4.5"
        />
      </button>

      <button
        v-if="showRulesTools"
        v-tippy="reportTooltipOptions"
        type="button"
        :class="[
          'flex size-6 shrink-0 items-center justify-center rounded-[6px] border border-[var(--color-gray-02)] shadow-[0_1px_2px_rgba(16,24,40,0.05)] transition-colors duration-150',
          props.showReportHighlights
            ? 'bg-[var(--color-gray-07)] text-[var(--color-white-01)]'
            : 'bg-[var(--color-white-01)] text-[var(--color-gray-07)] hover:bg-[var(--color-gray-09)]',
        ]"
        aria-label="Campo relatório"
        data-testid="report-field-btn"
        @click="emit('toggle-report-visibility')"
      >
        <IconImage :src="ChartIcon" alt="Campo relatório" class="size-4.5" />
      </button>
    </template>

    <template v-if="showFieldCount">
      <div class="h-5 w-px shrink-0 bg-[var(--color-gray-02)]" />

      <FormBuilderFieldCount
        :total="props.totalFields ?? 0"
        :scope="props.totalFieldsScope ?? 'matrix'"
      />
    </template>
  </footer>
</template>
